import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Autocomplete,
  TextField,
  Select,
  MenuItem,
  Button,
  Alert,
  CircularProgress,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { topupService } from "../../api";
import { useAlert } from "../../context/AlertContext";
import {
  getCountryOptions,
  getCurrencyForCountryOption,
  getDodoCurrencyOptions,
  isDodoSupportedCurrency,
  normalizeCountryLabel as normalize,
} from "../../utils/countryCurrency";

const COUNTRY_OPTIONS = getCountryOptions(); // [{ value: "AE", label: "United Arab Emirates" }, ...]
const CURRENCY_OPTIONS = getDodoCurrencyOptions();

// Per-country price management for a topup pack (edit mode only — needs an id).
// Uses GET /topup/:id/pricing (active + inactive rows),
// POST /topup/:id/pricing (upsert) and DELETE /topup/:id/pricing/:country
// (soft-disable). Prices are stored and rendered verbatim — no conversion.
const TopupCountryPricing = ({ topupId }) => {
  const { showAlert } = useAlert();
  const [pricing, setPricing] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState("");
  const [syncWarning, setSyncWarning] = useState("");

  // Add-new row (shown inside the table when adding === true). `country` holds
  // the full { value: isoCode, label } option — the ISO code resolves the
  // currency; only the label is ever sent to the backend.
  const [adding, setAdding] = useState(false);
  const [country, setCountry] = useState(null);
  const [currency, setCurrency] = useState("");
  const [price, setPrice] = useState("");

  // Inline edit of an existing row.
  const [editingCountry, setEditingCountry] = useState("");
  const [editPrice, setEditPrice] = useState("");

  const load = useCallback(async () => {
    if (!topupId) return;
    setLoading(true);
    try {
      const res = await topupService.getPricing(topupId);
      const rows = Array.isArray(res) ? res : res?.data;
      setPricing(Array.isArray(rows) ? rows : []);
    } catch (e) {
      /* leave as-is */
    } finally {
      setLoading(false);
    }
  }, [topupId]);

  useEffect(() => {
    load();
  }, [load]);

  // The endpoint returns active + inactive rows; only active ones are live.
  // Rows without the flag are treated as active so a missing field can't hide
  // configured pricing.
  const activePricing = useMemo(
    () => pricing.filter((p) => p?.isActive !== false),
    [pricing]
  );

  // A country that already has an active price can't be added again.
  const availableCountryOptions = useMemo(() => {
    const used = new Set(activePricing.map((p) => normalize(p.country)));
    return COUNTRY_OPTIONS.filter((c) => !used.has(normalize(c.label)));
  }, [activePricing]);

  // Upsert handles both create and edit (same endpoint, same country = update).
  const savePricing = async (payload, { onDone } = {}) => {
    setSyncWarning("");
    try {
      const res = await topupService.upsertPricing(topupId, {
        country: payload.country,
        currency: String(payload.currency).toUpperCase(),
        price: Number(payload.price),
      });
      if (res?.success === false) {
        showAlert(res?.message || "Could not save the price.", "error");
        return false;
      }
      // The plan pricing endpoint reports a payment-provider sync result
      // alongside the write. It is not documented for topups, so it is only
      // surfaced when the backend actually returns it.
      const sync = res?.data?.syncResult;
      if (sync && sync.success === false) {
        setSyncWarning(
          `Price saved, but it is NOT live on checkout yet: ${sync.reason || "Payment provider sync failed."}`
        );
      } else {
        showAlert(res?.message || "Country price saved.", "success");
      }
      if (onDone) onDone();
      load();
      return true;
    } catch (e) {
      showAlert(e?.message || "Could not save the price.", "error");
      return false;
    }
  };

  const currencyUnsupported = currency && !isDodoSupportedCurrency(currency);

  const resetAddRow = () => {
    setAdding(false);
    setCountry(null);
    setCurrency("");
    setPrice("");
  };

  const cancelEdit = () => {
    setEditingCountry("");
    setEditPrice("");
  };

  const handleCountryChange = (option) => {
    setCountry(option);
    // Auto-fill currency from the country's ISO code. If it can't be resolved
    // (no world-countries entry) or isn't Dodo-supported, leave it blank so the
    // admin picks one manually from the dropdown below instead of being blocked.
    const mapped = option ? getCurrencyForCountryOption(option) : "";
    setCurrency(mapped && isDodoSupportedCurrency(mapped) ? mapped : "");
  };

  const handleAdd = async () => {
    if (!country || String(price).trim() === "") {
      showAlert("Country, currency and price are all required.", "error");
      return;
    }
    if (!currency) {
      showAlert("Couldn't determine a currency for this country — please select one.", "error");
      return;
    }
    if (currencyUnsupported) {
      showAlert(
        `${currency} isn't supported by the payment provider, so ${country.label} can't be charged.`,
        "error"
      );
      return;
    }
    setSaving(true);
    await savePricing({ country: country.label, currency, price }, { onDone: resetAddRow });
    setSaving(false);
  };

  const startAdd = () => {
    cancelEdit();
    setAdding(true);
    setCountry(null);
    setCurrency("");
    setPrice("");
  };

  const startEdit = (entry) => {
    setEditingCountry(entry?.country);
    setEditPrice(String(entry?.price ?? ""));
    setSyncWarning("");
  };

  const handleSaveEdit = async (entry) => {
    if (String(editPrice).trim() === "") {
      showAlert("Price is required.", "error");
      return;
    }
    setSaving(true);
    await savePricing(
      { country: entry?.country, currency: entry?.currency, price: editPrice },
      { onDone: cancelEdit }
    );
    setSaving(false);
  };

  const handleDelete = async (entryCountry) => {
    setDeleting(entryCountry);
    try {
      const res = await topupService.disablePricing(topupId, entryCountry);
      if (res?.success === false) {
        showAlert(res?.message || "Could not remove the price.", "error");
        return;
      }
      showAlert(res?.message || "Country price removed.", "success");
      load();
    } catch (e) {
      showAlert(e?.message || "Could not remove the price.", "error");
    } finally {
      setDeleting("");
    }
  };

  if (!topupId) return null;

  return (
    <Box>
      <Typography sx={{ fontSize: "14px", fontWeight: 600, mb: 0.5 }}>
        Country Pricing
      </Typography>
      <Typography sx={{ fontSize: "12px", color: "#878787", mb: 1.5 }}>
        Each country has its own localized price, editable here. A pack with no
        price for a country is simply not offered there.
      </Typography>

      {syncWarning && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setSyncWarning("")}>
          {syncWarning}
        </Alert>
      )}

      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={startAdd}
          disabled={loading || adding || !!editingCountry}
          sx={{ textTransform: "none" }}
        >
          Add country
        </Button>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
          <CircularProgress size={22} />
        </Box>
      ) : activePricing.length === 0 && !adding ? (
        <Typography sx={{ fontSize: "13px", color: "#878787", mb: 2 }}>
          No active country prices yet.
        </Typography>
      ) : (
        <Table size="small" sx={{ mb: 2, tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              <TableCell sx={{ width: "40%" }}>Country</TableCell>
              <TableCell sx={{ width: "20%" }}>Currency</TableCell>
              <TableCell align="right" sx={{ width: "20%" }}>Price</TableCell>
              <TableCell align="right" sx={{ width: "20%" }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {activePricing.map((p) => {
              const isEditing = normalize(editingCountry) === normalize(p?.country);
              return (
                <TableRow key={p?.country}>
                  <TableCell>{p?.country}</TableCell>
                  {/* Currency is not editable on an existing row — it's fixed
                      once the price was created. */}
                  <TableCell>{String(p?.currency || "").toUpperCase()}</TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <TextField
                        size="small"
                        type="number"
                        value={editPrice}
                        onChange={(e) => setEditPrice(e.target.value)}
                        sx={{ width: "100%" }}
                        inputProps={{ min: 0, style: { textAlign: "right" } }}
                      />
                    ) : (
                      p?.price
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {isEditing ? (
                      <>
                        <IconButton
                          size="small"
                          disabled={saving}
                          onClick={() => handleSaveEdit(p)}
                        >
                          <CheckIcon fontSize="small" color="success" />
                        </IconButton>
                        <IconButton size="small" disabled={saving} onClick={cancelEdit}>
                          <CloseIcon fontSize="small" />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        <IconButton
                          size="small"
                          disabled={!!editingCountry || adding || deleting === p?.country}
                          onClick={() => startEdit(p)}
                        >
                          <EditOutlinedIcon fontSize="small" />
                        </IconButton>
                        <IconButton
                          size="small"
                          disabled={!!editingCountry || adding || deleting === p?.country}
                          onClick={() => handleDelete(p?.country)}
                        >
                          <DeleteOutlineIcon fontSize="small" color="error" />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {/* New editable row */}
            {adding && (
              <TableRow>
                <TableCell>
                  <Autocomplete
                    size="small"
                    options={availableCountryOptions}
                    getOptionLabel={(option) => option?.label || ""}
                    isOptionEqualToValue={(option, val) => option?.value === val?.value}
                    value={country}
                    onChange={(e, v) => handleCountryChange(v)}
                    sx={{ width: "100%" }}
                    renderInput={(params) => (
                      <TextField {...params} placeholder="Select country" />
                    )}
                  />
                </TableCell>
                {/* Currency auto-fills from the country when possible; the admin
                    can always override it, so an unresolved auto-fill never
                    blocks adding a price. */}
                <TableCell>
                  <Select
                    size="small"
                    displayEmpty
                    value={currency}
                    onChange={(e) => setCurrency(e.target.value)}
                    sx={{ width: "100%" }}
                    data-testid="pricing-currency-select"
                  >
                    <MenuItem value="" disabled>Select currency</MenuItem>
                    {CURRENCY_OPTIONS.map((c) => (
                      <MenuItem key={c} value={c}>{c}</MenuItem>
                    ))}
                  </Select>
                  {currencyUnsupported && (
                    <Typography sx={{ fontSize: "11px", color: "#d32f2f", lineHeight: 1.2 }}>
                      Not supported by payment provider
                    </Typography>
                  )}
                </TableCell>
                <TableCell align="right">
                  <TextField
                    size="small"
                    type="number"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    sx={{ width: "100%" }}
                    inputProps={{ min: 0, style: { textAlign: "right" } }}
                  />
                </TableCell>
                <TableCell align="right">
                  <IconButton
                    size="small"
                    disabled={saving || currencyUnsupported}
                    onClick={handleAdd}
                  >
                    <CheckIcon fontSize="small" color="success" />
                  </IconButton>
                  <IconButton size="small" disabled={saving} onClick={resetAddRow}>
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      )}
    </Box>
  );
};

export default TopupCountryPricing;
